import { notificationsService } from './notifications';
import { authService } from './auth';
import * as db from './db';

const FEED_KEY = 'betbuds_feed';
const MAX_FEED_SIZE = 500;

function getStored() {
  const stored = localStorage.getItem(FEED_KEY);
  if (stored) return JSON.parse(stored);
  localStorage.setItem(FEED_KEY, JSON.stringify([]));
  return [];
}

function save(data) {
  const trimmed = data.slice(-MAX_FEED_SIZE);
  localStorage.setItem(FEED_KEY, JSON.stringify(trimmed));
}

function parseJsonField(value, fallback) {
  if (value === null || value === undefined) return fallback;
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

function normalizeFeedItem(record) {
  if (!record) return record;
  return {
    ...record,
    id: record.feedItemId || record.id,
    activityId: record.activityId || null,
    metadata: parseJsonField(record.metadata, {}),
    reactions: parseJsonField(record.reactions, []),
    comments: parseJsonField(record.comments, []),
  };
}

function sortNewest(items) {
  return [...items].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
}

function getLocalForMasterEvent(masterEventId, limit) {
  return sortNewest(getStored().map(normalizeFeedItem).filter(Boolean).filter((f) => f.masterEventId === masterEventId)).slice(0, limit);
}

function getLocalForActivity(activityId, limit) {
  return sortNewest(getStored().map(normalizeFeedItem).filter(Boolean).filter((f) => f.activityId === activityId)).slice(0, limit);
}

function replaceLocalItems(predicate, items) {
  const normalizedItems = items.map(normalizeFeedItem).filter(Boolean);
  const remaining = getStored().map(normalizeFeedItem).filter(Boolean).filter((item) => !predicate(item));
  save([...remaining, ...normalizedItems]);
  return sortNewest(normalizedItems);
}

function saveLocalItem(item) {
  const normalized = normalizeFeedItem(item);
  const next = getStored().map(normalizeFeedItem).filter(Boolean).filter((f) => f.id !== normalized.id);
  next.push(normalized);
  save(next);
  return normalized;
}

function removeLocalItem(feedItemId) {
  save(getStored().map(normalizeFeedItem).filter(Boolean).filter((f) => f.id !== feedItemId));
}

function buildFeedPayload(item) {
  return {
    feedItemId: item.id,
    masterEventId: item.masterEventId,
    activityId: item.activityId,
    type: item.type,
    userId: item.userId,
    content: item.content,
    metadata: JSON.stringify(item.metadata || {}),
    reactions: JSON.stringify(item.reactions || []),
    comments: JSON.stringify(item.comments || []),
  };
}

export const feedService = {
  async getForMasterEvent(masterEventId, limit = 50) {
    try {
      const items = await db.listFeedItemsByEvent(masterEventId);
      return replaceLocalItems((item) => item.masterEventId === masterEventId, items).slice(0, limit);
    } catch (err) {
      console.warn('[feed] DynamoDB read failed, using local cache:', err);
      return getLocalForMasterEvent(masterEventId, limit);
    }
  },

  async getForActivity(activityId, limit = 30) {
    try {
      const items = await db.listFeedItemsByActivity(activityId);
      return replaceLocalItems((item) => item.activityId === activityId, items).slice(0, limit);
    } catch (err) {
      console.warn('[feed] DynamoDB read failed, using local cache:', err);
      return getLocalForActivity(activityId, limit);
    }
  },

  async post({ masterEventId, activityId, type, userId, content, metadata }) {
    const item = {
      id: `feed_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      masterEventId,
      activityId: activityId || null,
      type: type || 'custom',
      userId,
      content,
      metadata: metadata || {},
      reactions: [],
      comments: [],
      createdAt: new Date().toISOString(),
    };

    const saved = await db.createFeedItem(buildFeedPayload(item));
    const savedItem = saveLocalItem({ ...item, ...saved, id: saved?.feedItemId || item.id });

    // Auto-detect @ mentions and notify
    if (content) {
      const allUsers = authService.getAllUsers();
      const fromUser = allUsers.find((u) => u.id === userId);
      notificationsService.notifyMentions({
        text: content,
        fromUserId: userId,
        fromName: fromUser?.displayName || 'Someone',
        allUsers,
        masterEventId,
        activityId: activityId || null,
      });
    }

    return savedItem;
  },

  async addReaction(feedItemId, userId, emoji) {
    const feed = getStored().map(normalizeFeedItem);
    const item = feed.find((f) => f.id === feedItemId);
    if (!item) return;
    item.reactions = (item.reactions || []).filter((r) => r.userId !== userId);
    item.reactions.push({ userId, emoji, createdAt: new Date().toISOString() });
    const saved = await db.updateFeedItem(feedItemId, { reactions: JSON.stringify(item.reactions) });
    saveLocalItem({ ...item, ...saved, id: saved?.feedItemId || item.id });

    return item;
  },

  async addComment(feedItemId, userId, text) {
    const feed = getStored().map(normalizeFeedItem);
    const item = feed.find((f) => f.id === feedItemId);
    if (!item) return;
    if (!item.comments) item.comments = [];
    item.comments.push({ id: `fc_${Date.now()}`, userId, text, createdAt: new Date().toISOString() });
    const saved = await db.updateFeedItem(feedItemId, { comments: JSON.stringify(item.comments) });
    saveLocalItem({ ...item, ...saved, id: saved?.feedItemId || item.id });

    return item;
  },

  async delete(feedItemId) {
    await db.deleteFeedItem(feedItemId);
    removeLocalItem(feedItemId);
  },
};
